export interface Invoice {
  id: string
  vendor: string
  documentNo?: string
  documentType: string
  status: string
  team: string
  amount: string
  receivedDate: string
  documentDate: string
  dueDate?: string
  poNo?: string
  matchingType?: '2-way' | '3-way'
  tag?: string
  timestamp: string
  lockedBy?: string
}

export interface PurchaseOrderMatch {
  poNumber: string
  poLineNumber: number
  poLineDescription: string
  poUnitPrice: number
  poQuantity: number
  matchedQuantity: number
  remainingQuantity: number
  matchedAmount: number
  glCode: string
}

export interface GoodsReceiptMatch {
  grNumber: string
  grDate: string
  grQuantity: number
}

export interface LineItem {
  id: string
  itemCode: string
  description: string
  quantity: number
  unitPrice: number
  subtotal: number
  taxCode: string
  taxAmt: number
  total: number
  type: string
  notes?: string
  glCode?: string
  poMatch?: PurchaseOrderMatch
  grMatch?: GoodsReceiptMatch
}

export interface InvoiceDocument {
  id: string
  invoiceNumber: string
  invoiceDate: string
  dueDate: string
  receivedDate?: string
  currency: string
  supplier: string
  supplierABN?: string
  poNumber?: string
  matchingType?: '2-way' | '3-way' | 'non-po'
  subtotal: number
  taxAmt: number
  total: number
  notes?: string
  attachments?: string[]
  lineItems: LineItem[]
  imageUrl?: string
}

export interface InvoiceDetails {
  vendorName: string
  supplierCode: string
  vendorTaxNo: string
  email: string
  phoneNumber?: string
  vendorBankCheck?: boolean
  entity: string
  site: string
  address: string
  city: string
  state: string
  postcode: string
  country: string
  lastVerifiedDate?: string
  paymentTerms?: string
}

const invoiceImageUrl =
  'https://templates.invoicehome.com/invoice-template-us-neat-750px.png'

export const mockInvoices: Invoice[] = [
  {
    id: '1',
    vendor: 'Acme Corp',
    documentNo: 'INV-001',
    documentType: 'PO Invoice',
    status: 'Pending Review',
    team: 'AP Team',
    amount: '$12,500.00',
    receivedDate: '2024-01-15',
    documentDate: '2024-01-10',
    dueDate: '2024-02-10',
    poNo: 'PO-2024-001',
    matchingType: '3-way',
    timestamp: '2 hours ago',
  },
  {
    id: '2',
    vendor: 'Global Supplies',
    documentNo: 'INV-002',
    documentType: 'Non-PO Invoice',
    status: 'In Progress',
    team: 'Sarah Chen',
    amount: '$3,200.50',
    receivedDate: '2024-01-14',
    documentDate: '2024-01-12',
    dueDate: '2024-02-12',
    timestamp: '5 hours ago',
  },
  {
    id: '3',
    vendor: 'Tech Solutions',
    documentNo: 'INV-003',
    documentType: 'PO Invoice',
    status: 'Pending Review',
    team: 'AP Team',
    amount: '$8,750.00',
    receivedDate: '2024-01-13',
    documentDate: '2024-01-08',
    dueDate: '2024-02-08',
    poNo: 'PO-2024-003',
    matchingType: '2-way',
    timestamp: '1 day ago',
  },
  {
    id: '4',
    vendor: 'Office Plus',
    documentNo: 'INV-004',
    documentType: 'Non-PO Invoice',
    status: 'Exception',
    team: 'Mike Johnson',
    amount: '$1,450.25',
    receivedDate: '2024-01-12',
    documentDate: '2024-01-05',
    dueDate: '2024-02-05',
    timestamp: '1 day ago',
  },
  {
    id: '5',
    vendor: 'BuildRight Ltd',
    documentNo: 'INV-005',
    documentType: 'PO Invoice',
    status: 'Approved',
    team: 'Emma Wilson',
    amount: '$45,000.00',
    receivedDate: '2024-01-11',
    documentDate: '2024-01-03',
    dueDate: '2024-02-03',
    poNo: 'PO-2024-005',
    matchingType: '3-way',
    timestamp: '2 days ago',
  },
  {
    id: '6',
    vendor: 'Acme Corp',
    documentNo: 'CN-001',
    documentType: 'Credit Note',
    status: 'Pending Review',
    team: 'AP Team',
    amount: '-$2,500.00',
    receivedDate: '2024-01-10',
    documentDate: '2024-01-08',
    timestamp: '3 days ago',
  },
  {
    id: '7',
    vendor: 'Tech Solutions',
    documentNo: 'INV-007',
    documentType: 'PO Invoice',
    status: 'Pending Review',
    team: 'AP Team',
    amount: '$4,675.00',
    receivedDate: '2024-01-16',
    documentDate: '2024-01-15',
    dueDate: '2024-02-14',
    poNo: 'Multiple POs',
    matchingType: '2-way',
    tag: 'Multi-PO invoice',
    timestamp: '30 minutes ago',
  },
]

export const mockDocuments: Record<string, InvoiceDocument> = {
  '1': {
    id: '1',
    invoiceNumber: 'INV-001',
    invoiceDate: '10 Jan 2024',
    dueDate: '10 Feb 2024',
    receivedDate: '15 Jan 2024',
    currency: 'AUD',
    supplier: 'Acme Corp',
    supplierABN: '12 345 678 901',
    poNumber: 'PO-2024-001',
    matchingType: '3-way',
    subtotal: 11363.64,
    taxAmt: 1136.36,
    total: 12500,
    notes: 'Monthly supply order',
    imageUrl: invoiceImageUrl,
    lineItems: [
      {
        id: 'line-1-1',
        itemCode: 'WDG-001',
        description: 'Industrial Widgets',
        quantity: 500,
        unitPrice: 15,
        subtotal: 7500,
        taxCode: 'I',
        taxAmt: 750,
        total: 8250,
        type: 'Goods',
        glCode: '200-2010-BAU-A400-GLOBAL',
        poMatch: {
          poNumber: 'PO-2024-001',
          poLineNumber: 10,
          poLineDescription: 'Industrial Widgets',
          poUnitPrice: 15,
          poQuantity: 500,
          matchedQuantity: 500,
          remainingQuantity: 0,
          matchedAmount: 8250,
          glCode: '200-2010-BAU-A400-GLOBAL',
        },
        grMatch: {
          grNumber: 'GR-2024-0412',
          grDate: '09 Jan 2024',
          grQuantity: 500,
        },
      },
      {
        id: 'line-1-2',
        itemCode: 'BLT-002',
        description: 'Steel Bolts M10',
        quantity: 1000,
        unitPrice: 3.86364,
        subtotal: 3863.64,
        taxCode: 'I',
        taxAmt: 386.36,
        total: 4250,
        type: 'Goods',
        glCode: '200-2030-BAU-A400-GLOBAL',
        poMatch: {
          poNumber: 'PO-2024-001',
          poLineNumber: 20,
          poLineDescription: 'Steel Bolts M10',
          poUnitPrice: 3.86364,
          poQuantity: 1000,
          matchedQuantity: 1000,
          remainingQuantity: 0,
          matchedAmount: 4250,
          glCode: '200-2030-BAU-A400-GLOBAL',
        },
        grMatch: {
          grNumber: 'GR-2024-0413',
          grDate: '10 Jan 2024',
          grQuantity: 1000,
        },
      },
    ],
  },
  '2': {
    id: '2',
    invoiceNumber: 'INV-002',
    invoiceDate: '12 Jan 2024',
    dueDate: '12 Feb 2024',
    receivedDate: '14 Jan 2024',
    currency: 'AUD',
    supplier: 'Global Supplies',
    supplierABN: '98 765 432 109',
    matchingType: 'non-po',
    subtotal: 2909.55,
    taxAmt: 290.95,
    total: 3200.5,
    imageUrl: invoiceImageUrl,
    lineItems: [
      {
        id: 'line-2-1',
        itemCode: 'OFF-010',
        description: 'Office Furniture - Desk',
        quantity: 5,
        unitPrice: 450,
        subtotal: 2250,
        taxCode: 'I',
        taxAmt: 225,
        total: 2475,
        type: 'Goods',
      },
      {
        id: 'line-2-2',
        itemCode: 'OFF-011',
        description: 'Ergonomic Chair',
        quantity: 3,
        unitPrice: 219.85,
        subtotal: 659.55,
        taxCode: 'I',
        taxAmt: 65.95,
        total: 725.5,
        type: 'Goods',
      },
    ],
  },
  '3': {
    id: '3',
    invoiceNumber: 'INV-003',
    invoiceDate: '08 Jan 2024',
    dueDate: '08 Feb 2024',
    receivedDate: '13 Jan 2024',
    currency: 'AUD',
    supplier: 'Tech Solutions',
    supplierABN: '55 123 456 789',
    poNumber: 'PO-2024-003',
    matchingType: '2-way',
    subtotal: 7954.55,
    taxAmt: 795.45,
    total: 8750,
    imageUrl: invoiceImageUrl,
    lineItems: [
      {
        id: 'line-3-1',
        itemCode: 'SRV-001',
        description: 'IT Consulting Services',
        quantity: 40,
        unitPrice: 150,
        subtotal: 6000,
        taxCode: 'I',
        taxAmt: 600,
        total: 6600,
        type: 'Service',
        glCode: '400-4020-P006-A200-GLOBAL',
        poMatch: {
          poNumber: 'PO-2024-003',
          poLineNumber: 10,
          poLineDescription: 'IT Consulting Services',
          poUnitPrice: 150,
          poQuantity: 40,
          matchedQuantity: 40,
          remainingQuantity: 0,
          matchedAmount: 6600,
          glCode: '400-4020-P006-A200-GLOBAL',
        },
      },
      {
        id: 'line-3-2',
        itemCode: 'LIC-001',
        description: 'Software License Annual',
        quantity: 10,
        unitPrice: 195.455,
        subtotal: 1954.55,
        taxCode: 'I',
        taxAmt: 195.45,
        total: 2150,
        type: 'Service',
        glCode: '400-4020-P006-A310-GLOBAL',
        poMatch: {
          poNumber: 'PO-2024-003',
          poLineNumber: 20,
          poLineDescription: 'Software License Annual',
          poUnitPrice: 195.455,
          poQuantity: 10,
          matchedQuantity: 10,
          remainingQuantity: 0,
          matchedAmount: 2150,
          glCode: '400-4020-P006-A310-GLOBAL',
        },
      },
    ],
  },
  '4': {
    id: '4',
    invoiceNumber: 'INV-004',
    invoiceDate: '05 Jan 2024',
    dueDate: '05 Feb 2024',
    receivedDate: '12 Jan 2024',
    currency: 'AUD',
    supplier: 'Office Plus',
    supplierABN: '33 987 654 321',
    matchingType: 'non-po',
    subtotal: 1318.41,
    taxAmt: 131.84,
    total: 1450.25,
    notes: 'Stationery and supplies for Q1',
    imageUrl: invoiceImageUrl,
    lineItems: [
      {
        id: 'line-4-1',
        itemCode: 'STN-001',
        description: 'Printer Paper A4 (Box)',
        quantity: 20,
        unitPrice: 45,
        subtotal: 900,
        taxCode: 'I',
        taxAmt: 90,
        total: 990,
        type: 'Goods',
      },
      {
        id: 'line-4-2',
        itemCode: 'STN-002',
        description: 'Toner Cartridges',
        quantity: 5,
        unitPrice: 83.682,
        subtotal: 418.41,
        taxCode: 'I',
        taxAmt: 41.84,
        total: 460.25,
        type: 'Goods',
      },
    ],
  },
  '5': {
    id: '5',
    invoiceNumber: 'INV-005',
    invoiceDate: '03 Jan 2024',
    dueDate: '03 Feb 2024',
    receivedDate: '11 Jan 2024',
    currency: 'AUD',
    supplier: 'BuildRight Ltd',
    supplierABN: '77 456 789 012',
    poNumber: 'PO-2024-005',
    matchingType: '3-way',
    subtotal: 40909.09,
    taxAmt: 4090.91,
    total: 45000,
    imageUrl: invoiceImageUrl,
    lineItems: [
      {
        id: 'line-5-1',
        itemCode: 'CON-001',
        description: 'Concrete Supply - Grade 40',
        quantity: 50,
        unitPrice: 545.4546,
        subtotal: 27272.73,
        taxCode: 'I',
        taxAmt: 2727.27,
        total: 30000,
        type: 'Goods',
        glCode: '200-2010-BAU-A400-GLOBAL',
        poMatch: {
          poNumber: 'PO-2024-005',
          poLineNumber: 10,
          poLineDescription: 'Concrete Supply - Grade 40',
          poUnitPrice: 545.4546,
          poQuantity: 50,
          matchedQuantity: 50,
          remainingQuantity: 0,
          matchedAmount: 30000,
          glCode: '200-2010-BAU-A400-GLOBAL',
        },
        grMatch: {
          grNumber: 'GR-2024-0538',
          grDate: '02 Jan 2024',
          grQuantity: 50,
        },
      },
      {
        id: 'line-5-2',
        itemCode: 'CON-002',
        description: 'Steel Reinforcement',
        quantity: 20,
        unitPrice: 681.818,
        subtotal: 13636.36,
        taxCode: 'I',
        taxAmt: 1363.64,
        total: 15000,
        type: 'Goods',
        glCode: '200-2020-BAU-A400-GLOBAL',
        poMatch: {
          poNumber: 'PO-2024-005',
          poLineNumber: 20,
          poLineDescription: 'Steel Reinforcement',
          poUnitPrice: 681.818,
          poQuantity: 20,
          matchedQuantity: 20,
          remainingQuantity: 0,
          matchedAmount: 15000,
          glCode: '200-2020-BAU-A400-GLOBAL',
        },
        grMatch: {
          grNumber: 'GR-2024-0539',
          grDate: '02 Jan 2024',
          grQuantity: 20,
        },
      },
    ],
  },
  '6': {
    id: '6',
    invoiceNumber: 'CN-001',
    invoiceDate: '08 Jan 2024',
    dueDate: '08 Feb 2024',
    receivedDate: '10 Jan 2024',
    currency: 'AUD',
    supplier: 'Acme Corp',
    supplierABN: '12 345 678 901',
    matchingType: 'non-po',
    subtotal: -2272.73,
    taxAmt: -227.27,
    total: -2500,
    notes: 'Credit for returned widgets',
    imageUrl: invoiceImageUrl,
    lineItems: [
      {
        id: 'line-6-1',
        itemCode: 'WDG-001',
        description: 'Industrial Widgets (Returned)',
        quantity: -100,
        unitPrice: 22.7273,
        subtotal: -2272.73,
        taxCode: 'I',
        taxAmt: -227.27,
        total: -2500,
        type: 'Credit',
      },
    ],
  },
  '7': {
    id: '7',
    invoiceNumber: 'INV-007',
    invoiceDate: '15 Jan 2024',
    dueDate: '14 Feb 2024',
    receivedDate: '16 Jan 2024',
    currency: 'AUD',
    supplier: 'Tech Solutions',
    supplierABN: '55 123 456 789',
    poNumber: 'Multiple POs',
    matchingType: '2-way',
    subtotal: 4250,
    taxAmt: 425,
    total: 4675,
    notes:
      'Consolidated invoice allocated across three approved purchase orders.',
    imageUrl: invoiceImageUrl,
    lineItems: [
      {
        id: 'line-7-1',
        itemCode: 'HW-201',
        description: 'Network switches',
        quantity: 4,
        unitPrice: 500,
        subtotal: 2000,
        taxCode: 'I',
        taxAmt: 200,
        total: 2200,
        type: 'Goods',
        glCode: '400-4030-P012-A420-GLOBAL',
        poMatch: {
          poNumber: 'PO-2024-014',
          poLineNumber: 10,
          poLineDescription: 'Network switches',
          poUnitPrice: 500,
          poQuantity: 4,
          matchedQuantity: 4,
          remainingQuantity: 0,
          matchedAmount: 2200,
          glCode: '400-4030-P012-A420-GLOBAL',
        },
      },
      {
        id: 'line-7-2',
        itemCode: 'LIC-024',
        description: 'Security platform licences',
        quantity: 15,
        unitPrice: 100,
        subtotal: 1500,
        taxCode: 'I',
        taxAmt: 150,
        total: 1650,
        type: 'Service',
        glCode: '400-4030-P012-A310-GLOBAL',
        poMatch: {
          poNumber: 'PO-2024-019',
          poLineNumber: 30,
          poLineDescription: 'Security platform licences',
          poUnitPrice: 100,
          poQuantity: 15,
          matchedQuantity: 15,
          remainingQuantity: 0,
          matchedAmount: 1650,
          glCode: '400-4030-P012-A310-GLOBAL',
        },
      },
      {
        id: 'line-7-3',
        itemCode: 'SUP-118',
        description: 'On-site implementation support',
        quantity: 5,
        unitPrice: 150,
        subtotal: 750,
        taxCode: 'I',
        taxAmt: 75,
        total: 825,
        type: 'Service',
        glCode: '400-4020-P006-A200-GLOBAL',
        poMatch: {
          poNumber: 'PO-2024-022',
          poLineNumber: 20,
          poLineDescription: 'On-site implementation support',
          poUnitPrice: 150,
          poQuantity: 5,
          matchedQuantity: 5,
          remainingQuantity: 0,
          matchedAmount: 825,
          glCode: '400-4020-P006-A200-GLOBAL',
        },
      },
    ],
  },
}

export function getDocumentDetails(doc: InvoiceDocument): InvoiceDetails {
  const vendorDetails: Record<string, Partial<InvoiceDetails>> = {
    'Acme Corp': {
      supplierCode: 'ACM-001',
      vendorTaxNo: '12 345 678 901',
      email: 'accounts@acmecorp.com.au',
      phoneNumber: '+61 2 9876 5432',
      entity: 'Acme Corporation Pty Ltd',
      site: 'Sydney HQ',
      address: '100 George Street',
      city: 'Sydney',
      state: 'NSW',
      postcode: '2000',
      country: 'Australia',
      vendorBankCheck: true,
      lastVerifiedDate: '15 Dec 2023',
      paymentTerms: 'Net 30',
    },
    'Global Supplies': {
      supplierCode: 'GLS-002',
      vendorTaxNo: '98 765 432 109',
      email: 'billing@globalsupplies.com',
      phoneNumber: '+61 3 1234 5678',
      entity: 'Global Supplies Pty Ltd',
      site: 'Melbourne Office',
      address: '50 Collins Street',
      city: 'Melbourne',
      state: 'VIC',
      postcode: '3000',
      country: 'Australia',
      vendorBankCheck: true,
      lastVerifiedDate: '20 Dec 2023',
      paymentTerms: 'Net 14',
    },
    'Tech Solutions': {
      supplierCode: 'TEC-003',
      vendorTaxNo: '55 123 456 789',
      email: 'invoices@techsolutions.com.au',
      phoneNumber: '+61 7 9876 1234',
      entity: 'Tech Solutions Australia',
      site: 'Brisbane Office',
      address: '200 Queen Street',
      city: 'Brisbane',
      state: 'QLD',
      postcode: '4000',
      country: 'Australia',
      vendorBankCheck: true,
      lastVerifiedDate: '10 Jan 2024',
      paymentTerms: 'Net 30',
    },
    'Office Plus': {
      supplierCode: 'OFP-004',
      vendorTaxNo: '33 987 654 321',
      email: 'accounts@officeplus.com.au',
      entity: 'Office Plus Pty Ltd',
      site: 'Perth Warehouse',
      address: '75 St Georges Terrace',
      city: 'Perth',
      state: 'WA',
      postcode: '6000',
      country: 'Australia',
      vendorBankCheck: false,
      paymentTerms: 'Net 7',
    },
    'BuildRight Ltd': {
      supplierCode: 'BRL-005',
      vendorTaxNo: '77 456 789 012',
      email: 'finance@buildright.com.au',
      phoneNumber: '+61 2 5555 1234',
      entity: 'BuildRight Limited',
      site: 'Sydney Construction',
      address: '300 Pitt Street',
      city: 'Sydney',
      state: 'NSW',
      postcode: '2000',
      country: 'Australia',
      vendorBankCheck: true,
      lastVerifiedDate: '05 Jan 2024',
      paymentTerms: 'Net 45',
    },
  }

  const details = vendorDetails[doc.supplier] || {}

  return {
    vendorName: doc.supplier,
    supplierCode: details.supplierCode || 'N/A',
    vendorTaxNo: details.vendorTaxNo || doc.supplierABN || 'N/A',
    email: details.email || 'N/A',
    phoneNumber: details.phoneNumber,
    vendorBankCheck: details.vendorBankCheck,
    entity: details.entity || 'N/A',
    site: details.site || 'N/A',
    address: details.address || 'N/A',
    city: details.city || 'N/A',
    state: details.state || 'N/A',
    postcode: details.postcode || 'N/A',
    country: details.country || 'Australia',
    lastVerifiedDate: details.lastVerifiedDate,
    paymentTerms: details.paymentTerms,
  }
}

export function formatAmount(amount: number, currency: string = 'AUD'): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency,
  }).format(amount)
}
